<template>
    <div :class="stylePanel" @dragover.prevent="handleDragOver">
        <CompassPanel
            :ref="refPanel"
            v-model="showPanel"
            size="large"
            :title="getPanelTitle"
            icon="obj-slsy-sent-email"
            enableShrink
            @toggled="toggleState"
            @closed="closePanel"
        >
            <div v-show="!isPanelMinimized" class="email-panel__content">
                <EmailRecipientsInputs
                    :hashPanel="hashPanel"
                    :recipients="recipients"
                    :isUpdatingRecipients="isUpdatingRecipients"
                    class="email-recipients-inputs"
                    @updateAddressesEmails="onUpdateAddressesEmails"
                    @updateRecipient="isUpdatingRecipients = false"
                />
                <div class="email-panel__content-subject">
                    <EmailSubjectInput
                        :subject="email.subject"
                        @update:subject="
                            (newSubject) => {
                                email.subject = newSubject;
                            }
                        "
                    />
                </div>
                <EmailRelatedObject v-if="activeLinked.name" :object="activeLinked" />
                <RichText
                    ref="refEmailPanelRichText"
                    v-model="email.content"
                    :options="optionsRichText"
                    :isCustom="true"
                    :showRichTextMenu="false"
                    :showAIComponents="isSellsyAIEnabled"
                    class="email-richtext"
                    typeRichText="email"
                    :hash="hashPanel"
                    @focus="isEditorFocused = true"
                    @blur="isEditorFocused = false"
                >
                    <template v-if="isDragging" #drag-handle>
                        <div
                            class="drag-text typographies-paragraph-large-bold"
                            @dragleave.stop="handleDragLeave"
                            @drop.prevent="handleDrop"
                        >
                            {{ labelDrag }}
                        </div>
                    </template>
                </RichText>

                <template v-for="(item, index) in attachments">
                    <EmailAttachments
                        v-if="attachments.length"
                        :key="index"
                        :attachment="item"
                        @removeItem="removeAttachment"
                    />
                </template>
            </div>
            <template #footer>
                <div v-show="!isPanelMinimized" class="email-panel__footer">
                    <RichTextMenu
                        v-if="richTextEditor"
                        :editorMenu="richTextEditor"
                        :options="optionsRichText"
                        typeRichText="email"
                        :disabled="isPanelMinimized"
                        :hash="hashPanel"
                        class="email-richtext-menu"
                    >
                        <template #actions>
                            <EmailCustomTemplateToolbar
                                v-if="showCustomTemplates"
                                :hashPanel="hashPanel"
                                :linkedId="linkedId"
                                :linkedType="linkedType"
                                typePanel="newPanel"
                            />
                        </template>
                    </RichTextMenu>
                    <div class="email-panel__footer-container">
                        <div
                            v-if="showHint"
                            class="email-panel__ai-hint typographies-paragraph-small-regular"
                            v-html="$t.__('richtext.ai.hint')"
                        />
                        <div class="container-actions">
                            <CompassButton
                                :label="$t.__('common.actions.delete')"
                                type="secondary"
                                @click="closePanel"
                            />
                            <template v-if="isFeatureScheduleEmailEnabled">
                                <CompassTooltip
                                    :disabled="canSendEmail"
                                    :content="
                                        $t.__('email.EmailPanel.error_reconnect_email_account')
                                    "
                                    placement="top-end"
                                >
                                    <CompassSplitButton
                                        ref="SplitSendButton"
                                        :label="$t.__('common.actions.send')"
                                        :loading="isLoading"
                                        :disabled="!canSendEmail"
                                        @clickOnLabel="onSendEmail"
                                        @clickOnArrow="showDropdown = !showDropdown"
                                    />
                                </CompassTooltip>
                                <CompassDropdown
                                    parentRef="SplitSendButton"
                                    :isVisible="showDropdown"
                                    placement="top-end"
                                    dropdownClass="email-panel__schedule-dropdown"
                                    @onVisibilityChanged="
                                        (visibility) => handleVisibleChange(visibility)
                                    "
                                    @itemSelected="openScheduleEmail"
                                >
                                    <CompassDropdownOption
                                        :label="$t.__('email.ScheduleEmailDialog.title')"
                                        value="scheduleAction"
                                        icon="calendar-reschedule"
                                        :isCheckable="false"
                                        @click="openScheduleEmail"
                                    />
                                </CompassDropdown>
                                <ScheduleEmailDialog
                                    :visible="showScheduleEmailDialog"
                                    @scheduled="handleSendScheduledEmail"
                                    @close="showScheduleEmailDialog = false"
                                />
                            </template>
                            <CompassTooltip
                                v-else
                                :disabled="canSendEmail"
                                :content="$t.__('email.EmailPanel.error_reconnect_email_account')"
                                placement="top-end"
                            >
                                <CompassButton
                                    :label="$t.__('common.actions.send')"
                                    :loading="isLoading"
                                    :disabled="!canSendEmail"
                                    @click="onSendEmail"
                                />
                            </CompassTooltip>
                        </div>
                    </div>
                </div>
            </template>
        </CompassPanel>
        <CompassDialog
            :visible.sync="shouldDisplayErrorDialog"
            size="small"
            :title="$t.__('email.emailPanels.EmailPanel.error')"
            :primaryAction="primaryAction"
            :secondaryActions="secondaryActions"
        >
            <div class="typographies-paragraph-medium-regular">
                {{ textDialogError }}
            </div>
        </CompassDialog>
        <CompassDialog
            :visible.sync="shouldDisplayEmptyRecipientsWarningDialog"
            :title="$t.__('email.emailPanels.EmailPanel.empty_recipients_warning_dialog.title')"
            size="small"
            :primaryAction="primaryActionEmptyRecipientsWarningDialog"
            :secondaryActions="secondaryActionsEmptyRecipientsWarningDialog"
        >
            <p
                class="typographies-paragraph-medium-regular"
                v-html="emptyRecipientsWarningDialogText"
            />
        </CompassDialog>
    </div>
</template>

<script>
import EmailRecipientsInputs from 'vcomponents/email/emailPanels/EmailRecipientsInputs.vue';
import EmailSubjectInput from 'vcomponents/email/emailPanels/EmailSubjectInput.vue';
import RichText from 'vcomponents/richtext/RichText.vue';
import RichTextMenu from 'vcomponents/richtext/RichTextMenu.vue';
import { sendEmail } from 'api/emailEngine/emailEngineService';
import Feature from 'Services/Feature';
import { createNamespacedHelpers } from 'vuex';
import {
    getEmailSettings,
    getPrefillInfo,
    getRecomendUsers,
} from 'api/emailEngine/emailEngineService';
import EmailRelatedObject from 'vcomponents/email/EmailRelatedObject.vue';
import EmailAttachments from 'vcomponents/email/emailPanels/EmailAttachments.vue';
import EmailCustomTemplateToolbar from 'vcomponents/email/emailCustomTemplate/EmailCustomTemplateToolbar.vue';
import StoreAI from 'stores/ai';
import ScheduleEmailDialog from './ScheduleEmailDialog.vue';
import generateHash from 'Helpers/hashGenerator.js';
import mime from 'mime';
import { isMarginEnabled } from 'Helpers/richTextHelper';
import RichTextMixin from 'mixins/RichTextMixin';
import RichTextAttachmentsMixin from 'mixins/RichTextAttachmentsMixin';
import EmailEditorMixins from 'mixins/emails/EmailEditorMixins';

const { mapActions } = createNamespacedHelpers('user');
const { mapState: mapStateUser } = createNamespacedHelpers('user');

export default {
    components: {
        EmailRecipientsInputs,
        EmailSubjectInput,
        RichText,
        RichTextMenu,
        EmailRelatedObject,
        EmailAttachments,
        EmailCustomTemplateToolbar,
        ScheduleEmailDialog,
    },
    mixins: [RichTextMixin, RichTextAttachmentsMixin, EmailEditorMixins],
    props: {
        isPanelMinimized: {
            type: Boolean,
            default: false,
        },
        hashPanel: {
            type: String,
            required: true,
        },
        prefill: {
            type: Boolean,
            default: false,
        },
        linkEmail: {
            type: String,
            default: '',
        },
        linkedId: {
            type: [String, Number],
            default: '',
        },
        linkedType: {
            type: String,
            default: '',
        },
        doclabel: {
            type: String,
            default: '',
        },
        docFile: {
            type: Object,
            default: () => {},
        },
        signature: {
            type: String,
            default: '',
        },
        linkedObject: {
            type: Object,
            default: () => {},
        },
        resendAttachments: {
            type: Array,
            default: () => [],
        },
        description: {
            type: String,
            default: '',
        },
        resendDatas: {
            type: Object,
            default: () => {},
        },
        template: {
            type: Object,
            default: () => {},
        },
        showCustomTemplates: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            refPanel: null,
            showPanel: false,
            defaultPanelTitle: this.$t.__('email.EmailPanel.default_panel_title'),
            isRecipientUpdate: false,
            recommendedUsers: [],
            activeLinked: {},
            fileInfo: {},
            userType: false,
            prefillData: {},
            attachments: [],
            email: {
                subject: '',
                addresses: [],
                content: '',
                signature: '',
            },
            recipients: {
                to: [],
                cc: [],
                bcc: [],
            },
            richTextEditor: null,
            emailTo: [],
            optionsRichText: [
                'BlockquoteTiptap',
                'SellsyAI',
                'Heading',
                'Bold',
                'Italic',
                'Underline',
                'Color',
                'Highlight',
                'BulletList',
                'OrderedList',
                'TextAlign',
                'Divider',
                'Link',
                'ImageResizable',
                'Attachment',
                'HeadingTiptap',
                'SizeTiptap',
            ],
            isLoading: false,
            objectTypeMapper: {
                document: 'document',
                third: 'company',
                people: 'contact',
                opportunity: 'opportunity',
                company: 'company',
                contact: 'contact',
                individual: 'individual',
            },
            priorityMapping: {
                document: 1, // Highest priority
                opportunity: 2,
                contact: 3,
                individual: 4,
                company: 5,
                staff: 6,
                client: 7,
                prospect: 7,
                supplier: 7, // Lowest priority
            },
            isUpdatingRecipients: false,
            labelDrag: this.$t.__('email.EmailPanel.label_drag'),
            isDragging: false,
            labelSucessEmailSend: this.$t.__('email.common.label_sucess_email_send'),
            labelSuccessScheduledEmailSend: this.$t.__(
                'email.common.label_sucess_scheduled_email_send',
            ),
            originalEmailContent: '',
            isEditorFocused: false,
            showDropdown: false,
            showScheduleEmailDialog: false,
            scheduledDate: null,
            canSendEmail: true,
        };
    },
    computed: {
        ...mapStateUser({
            userForename: 'forename',
            name: 'name',
            userEmail: 'email',
        }),
        isSellsyAIEnabled() {
            return Feature.isEnabled('sellsy-ai');
        },
        isFeatureScheduleEmailEnabled() {
            return Feature.isEnabled('email-engine-scheduled-emails');
        },
        showHint() {
            return this.isEditorFocused && isMarginEnabled();
        },
        informations() {
            return this.$store.getters.dataFetch;
        },
        isFeatureEmailEnabled() {
            return Feature.isEnabled('email-engine');
        },
        identity() {
            return this.$store.getters.identity;
        },
        stylePanel() {
            return ['email-panel', { minimized: this.isPanelMinimized }];
        },
        getPanelTitle() {
            return this.email.subject !== '' ? this.email.subject : this.defaultPanelTitle;
        },
        isParticular() {
            return (
                ['client', 'prospect', 'supplier'].includes(this.identity && this.identity.type) &&
                !this.identity.isCorporation
            );
        },
        isIndividual() {
            return this.userType || this.isParticular;
        },
        isResend() {
            return (
                this.resendDatas.resendSubject &&
                Object.keys(this.resendDatas.resendRecipients).length > 0
            );
        },
    },
    watch: {
        email: {
            handler() {
                if (this.isFeatureEmailEnabled) {
                    this.saveEmailData();
                }
            },
            deep: true,
        },
        'email.addresses': {
            handler(newEmailAddresses) {
                this.emailTo = newEmailAddresses.filter((address) => address.type === 'to');
                this.recipients.to = [...this.emailTo];
                this.updateLinkedObject(this.emailTo);
            },
            deep: true,
        },
        isPanelMinimized(newValue) {
            this.$nextTick(() => {
                if (this.$refs[`${this.refPanel}`]) {
                    this.$refs[`${this.refPanel}`].isMinimized = newValue;
                }
            });
        },
        attachments() {
            for (const attachment of this.attachments) {
                if (!attachment.tempId) {
                    attachment.tempId = generateHash();
                }
            }
            if (this.isFeatureEmailEnabled) {
                this.saveEmailData();
            }
        },
    },
    created() {
        if (!this.$store.state[`ai-${this.hashPanel}`] && this.isSellsyAIEnabled) {
            this.$store.registerModule(`ai-${this.hashPanel}`, StoreAI);
        }
        if (this.docFile && Object.keys(this.docFile).length) {
            const parsedFile = {
                name: this.docFile.name,
                size: this.docFile.size,
                extension: this.docFile.extension,
                content_id: '',
                content_type: 'application/pdf',
                content_disposition: 'attachment',
                public_link: this.docFile.public_link,
                default: true,
            };
            if (this.docFile.related.length) {
                parsedFile.relatedId = this.docFile.related[0].id;
                parsedFile.relatedType = this.docFile.related[0].type;
            }
            this.updateAttachmentList(parsedFile);
        }

        if (this.template && this.template.attachments && this.template.attachments.length) {
            for (const attachment of this.template.attachments) {
                this.updateAttachmentList({
                    name: attachment.name,
                    size: attachment.size,
                    extension: attachment.extension,
                    content_id: '',
                    content_type: mime.getType(attachment.extension),
                    content_disposition: 'attachment',
                    public_link: attachment.public_link,
                    default: true,
                });
            }
        }

        EventBus.on(`richText-${this.hashPanel}:onAddTag`, (payload) => {
            this.updateLinkedObject([payload]);
        });
        EventBus.on(`richText-${this.hashPanel}:addAttachment`, (file) => {
            this.updateAttachmentList(file);
        });
        EventBus.on(`richText-${this.hashPanel}:loadingAttachments`, (fileInfo) => {
            this.attachments.push(fileInfo);
        });
        if (this.isFeatureEmailEnabled) {
            this.loadEmailData();
            if (this.email.addresses.length && this.email.addresses.to) {
                this.recipients.to = [];
                this.recipients.to.push(...this.email.addresses.to);
                this.isUpdatingRecipients = true;
            }
            this.getSettings();
        }
        this.refPanel = `refPanel-${this.hashPanel}`;
        this.showPanel = true;
        this.manageMinimize();
    },
    mounted() {
        this.loadrecommendedUsers();
        this.manageMinimize();
        this.richTextEditor = this.$refs.refEmailPanelRichText.editor;
        if (this.isResend) {
            this.recipients = this.resendDatas.resendRecipients;
            this.attachments = this.resendAttachments;
            this.email.subject = this.resendDatas.resendSubject;
            this.activeLinked = {
                name: this.linkedObject.name,
                type: this.linkedObject.type,
                id: this.linkedObject.id,
            };
            this.isUpdatingRecipients = true;
        }
        if (this.isFeatureEmailEnabled) {
            if (!this.email.signature) {
                this.email.signature = this.signature;

                // inject signature HTML when template is reminderMandate or document (use template email by default)
                if (
                    this.template?.type === 'reminderMandate' ||
                    this.template?.type === 'document' ||
                    this.template?.type === 'manual_document_reminder' ||
                    this.template?.type === 'proposal'
                ) {
                    this.richTextEditor
                        .chain()
                        .focus('end')
                        .insertContent('<p></p><p></p>')
                        .insertCustomSignature({
                            signature: this.email.signature,
                        })
                        .blur()
                        .run();
                }
            }
        }
        if (this.prefill && !this.email.addresses.length) {
            this.getPrefillData();
            this.updateLinkedObject(this.email.addresses);
        }
        if (this.linkEmail) {
            this.generateEmailToPrefill(this.linkEmail);
        }
        this.$nextTick(() => {
            if (!this.email.content) {
                this.richTextEditor.commands.focus('start');
            }
        });
    },
    beforeDestroy() {
        EventBus.off(`richText-${this.hashPanel}:onAddTag`);
        EventBus.off(`richText-${this.hashPanel}:addAttachment`);
        EventBus.off(`richText-${this.hashPanel}:loadingAttachments`);
    },
    destroyed() {
        if (this.$store.state[`ai-${this.hashPanel}`] && this.isSellsyAIEnabled) {
            this.$store.unregisterModule(`ai-${this.hashPanel}`, StoreAI);
        }
    },
    methods: {
        updateLinkedObject(filledEmail) {
            if (!this.isResend) {
                if (this.linkedType) {
                    this.activeLinked = {
                        name: this.doclabel,
                        type: this.linkedType,
                        id: this.linkedId,
                    };
                } else {
                    if (filledEmail && filledEmail.length) {
                        const prefilledEmail = {
                            name: filledEmail[0].name,
                            type:
                                this.objectTypeMapper[
                                    filledEmail[0] &&
                                        filledEmail[0].related &&
                                        filledEmail[0].related[0].type
                                ] ||
                                (filledEmail[0] &&
                                    filledEmail[0].related &&
                                    filledEmail[0].related[0].type) ||
                                '',
                            id: filledEmail[0].id,
                        };
                        const activePageEmail = {
                            name:
                                this.objectTypeMapper[this.$route.name?.split('.')[0]] ===
                                'opportunity'
                                    ? this.informations.informations.find(
                                          (index) => index.key === 'ident',
                                      ).value
                                    : this.identity?.name,
                            type: this.objectTypeMapper[this.$route.name?.split('.')[0]],
                            id: this.identity?.id,
                        };
                        this.activeLinked = this.chooseHigherPriorityObject(
                            activePageEmail,
                            prefilledEmail,
                        );
                    } else {
                        this.activeLinked = {
                            name: this.identity?.name,
                            type: this.objectTypeMapper[this.$route.name?.split('.')[0]],
                            id: this.identity?.id,
                        };
                    }
                }
            }
        },
        chooseHigherPriorityObject(obj1, obj2) {
            const type1Priority = this.priorityMapping[obj1.type];
            const type2Priority = this.priorityMapping[obj2.type];

            return type1Priority < type2Priority ? obj1 : obj2;
        },
        manageMinimize() {
            if (this.isPanelMinimized) {
                this.$nextTick(() => {
                    if (this.$refs[`${this.refPanel}`]) {
                        this.$refs[`${this.refPanel}`].isMinimized = true;
                    }
                });
            }
        },
        ...mapActions(['setrecommendedUsers']),
        loadrecommendedUsers() {
            const objectType = this.isIndividual ? 'individual' : this.$route.name?.split('.')[0];
            const data = {
                id: this.linkedId ?? (parseInt(this.$route.params.id) || this.email.id),
                type: this.linkedType ?? this.objectTypeMapper[objectType],
            };
            if (data.id && data.type) {
                getRecomendUsers(data.id, data.type).then((response) => {
                    if (response.data) {
                        const mappedData = response.data.map((item) => ({
                            email: item.email,
                            firstName: item.name,
                            lastName: '',
                            position: item.relatedObject.type === 'staff' ? null : item.position,
                            id: item.relatedObject.id,
                            type: item.relatedObject.type,
                            avatar: item.avatar,
                        }));
                        this.setrecommendedUsers(mappedData);

                        // If we have a system template to add (like GoCardless), try and add the main contact to the recipients
                        if (
                            this.template &&
                            this.template.type === 'reminderMandate' &&
                            response.data[0].relatedObject.type.includes('client')
                        ) {
                            if (!this.recipients.to.includes(response.data[0].email)) {
                                this.recipients.to.push(response.data[0]);
                                this.isUpdatingRecipients = true;
                            }
                        }
                    }
                });
            }
        },
        loadEmailData() {
            try {
                const emailData = localStorage.getItem(`emailData_${this.hashPanel}`);
                if (emailData) {
                    const email = JSON.parse(emailData);
                    this.email.subject = email.subject;
                    this.email.content = email.content;

                    this.deleteRecipients();
                    for (const address of email.addresses) {
                        if (address.type === 'cc') {
                            this.recipients.cc.push(address);
                        } else if (address.type === 'bcc') {
                            this.recipients.bcc.push(address);
                        } else {
                            this.recipients.to.push(address);
                        }
                    }
                    if (email.attachments) {
                        this.attachments = email.attachments;
                    }
                } else {
                    if (this.template?.subject) {
                        this.email.subject = this.template.subject;
                    }
                    if (this.template?.content) {
                        this.email.content = this.template.content;
                    }
                }
            } catch (error) {
                // if json parse error, clear local storage (to delete old ugly data)
                localStorage.removeItem(`emailData_${this.hashPanel}`);
            }
        },

        deleteRecipients() {
            this.recipients = {
                to: [],
                cc: [],
                bcc: [],
            };
        },

        saveEmailData() {
            const cleanContent = this.removeAITags(this.email.content);
            localStorage.setItem(
                `emailData_${this.hashPanel}`,
                JSON.stringify({
                    description: this.email.description,
                    subject: this.email.subject,
                    addresses: this.email.addresses,
                    content: cleanContent,
                    isIndividual: this.isIndividual,
                    id: this.$route.params.id,
                    attachments: this.attachments,
                }),
            );
        },
        generateEmailToPrefill(email) {
            const emailTo = {
                email: email,
                name: '',
            };
            this.recipients.to.push(emailTo);
            this.isUpdatingRecipients = true;
        },
        getPrefillData() {
            const objectType = this.isIndividual ? 'individual' : this.$route.name?.split('.')[0];
            const data = {
                id: this.linkedId ?? parseInt(this.$route.params.id),
                type: this.linkedType ?? this.objectTypeMapper[objectType],
                context: this.template?.type ?? '',
            };
            if (data.id && data.type) {
                const params = {};
                if (data.context === 'manual_document_reminder') {
                    params.context = data.context;
                }
                getPrefillInfo(data.id, data.type, params).then((response) => {
                    if (response.data.length) {
                        this.prefillData = response.data[0];
                        this.activeLinked = {
                            name: this.prefillData.name,
                            type: this.isIndividual
                                ? 'individual'
                                : this.prefillData.relatedObject.type,
                            id: this.prefillData.relatedObject.id,
                        };
                        this.emailTo = response.data.map((item) => ({
                            email: item.email,
                            name: item.name,
                            related: [
                                {
                                    id: item.relatedObject.id,
                                    type: this.isIndividual
                                        ? 'individual'
                                        : this.objectTypeMapper[item.relatedObject.type],
                                },
                            ],
                        }));
                        this.recipients.to.push(...this.emailTo);
                        this.isUpdatingRecipients = true;
                    }
                });
            }
        },
        async getSettings() {
            this.canSendEmail = false;
            try {
                const { data } = await getEmailSettings();

                this.canSendEmail = !!data.connection;

                if (data.connection.type && data.connection.type === 'transfer') {
                    this.$router.push({
                        name: 'onboarding.email',
                        query: {
                            invalid_onboarding: 'transfer',
                        },
                    });
                }

                if (data.add_sender_to_email) {
                    const senderToEmail = {
                        email: data.connection.email ?? this.userEmail,
                        name: this.userForename,
                    };
                    this.recipients.bcc.push(senderToEmail);
                    this.isUpdatingRecipients = true;
                }

                if (!this.email.content) {
                    this.email.content = this.isResend ? this.description : '';

                    const { signature } = data;
                    if (signature?.computed && !this.isResend) {
                        this.richTextEditor
                            .chain()
                            .insertContent('<p></p><p></p>')
                            .insertCustomSignature({
                                signature: signature.computed,
                            })
                            .run();
                        this.richTextEditor.commands.focus('start');
                        this.email.signature = signature.computed;
                    }
                }
                this.saveEmailData();
            } catch (error) {
                this.canSendEmail = false;
                this.$pushToast({
                    type: 'error',
                    icon: true,
                    text: error,
                });
            }
        },
        onUpdateAddressesEmails(addresses) {
            this.email.addresses = addresses;
        },
        closePanel() {
            localStorage.removeItem(`emailData_${this.hashPanel}`);
            this.$emit('close-panel');
            this.showPanel = false;
        },
        toggleState() {
            this.$emit('toggle-panel-state');
        },
        handleSendScheduledEmail(scheduledDate) {
            this.scheduledDate = scheduledDate;
            this.showScheduleEmailDialog = false;
            this.onSendEmail();
        },
        onSendEmail() {
            EventBus.emit('validateEmail');
            if (window.pendo?.accountId && this.isSellsyAIEnabled) {
                window.pendo.track('email_v3_sent', {
                    ai: this.$store.state[`ai-${this.hashPanel}`].useAI,
                });
            }

            this.$nextTick(() => {
                if (this.areRecipientsValid()) {
                    this.sendEmail();
                }
            });
        },
        getRelatedObject() {
            const id = parseInt(this.$route.params.id);
            const objectType = this.isIndividual ? 'individual' : this.$route.name?.split('.')[0];
            if (objectType && this.objectTypeMapper[objectType] && id > 0) {
                return [{ id: id, type: this.objectTypeMapper[objectType] }];
            } else if (this.linkedId && this.linkedType) {
                return [{ id: +this.linkedId, type: this.linkedType }];
            }
            return [];
        },
        async sendEmail() {
            const recipientsTo = this.getRecipentsByType('to');
            const recipientsCC = this.getRecipentsByType('cc');
            const recipientsBCC = this.getRecipentsByType('bcc');
            const related = this.getRelatedObject();
            const emailContentWithoutNodeSignature = this.email.content.replace(
                /<customsignature[\s\S]*?<\/customsignature>/g,
                this.email.signature,
            );
            const dataToSend = {
                subject: this.email.subject,
                content: emailContentWithoutNodeSignature,
                to: recipientsTo,
                cc: recipientsCC,
                bcc: recipientsBCC,
                related: related,
                attachments: this.attachments,
            };

            if (this.isResend) {
                dataToSend.email_id = this.resendDatas.objectid;
            }

            if (this.scheduledDate) {
                dataToSend.scheduled_at = this.scheduledDate;
            }

            this.isLoading = true;

            await sendEmail(dataToSend)
                .then(() => {
                    const toastOptions = {
                        type: 'success',
                        icon: true,
                        text: this.labelSucessEmailSend,
                        close: true,
                    };
                    if (this.scheduledDate) {
                        toastOptions.text = this.labelSuccessScheduledEmailSend;
                        toastOptions.action = {
                            text: this.$t.__('common.labels.see'),
                            callback: () => {
                                this.$router.push({
                                    name: 'emails.scheduled',
                                });
                            },
                        };
                    }

                    this.$pushToast(toastOptions);
                    this.isFeatureEmailEnabled &&
                        window.EventBus.emit('timeline:addMockedLog', dataToSend);
                    this.closePanel();
                })
                .catch((error) => {
                    this.$pushToast({
                        type: 'error',
                        icon: true,
                        text: error,
                        close: true,
                    });
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
        handleDragOver() {
            this.isDragging = true;
            this.richTextEditor.setEditable(false);
        },
        handleDragLeave() {
            this.isDragging = false;
            this.richTextEditor.setEditable(true);
        },
        async handleDrop(event) {
            this.isDragging = false;
            this.richTextEditor.setEditable(true);
            const files = event.dataTransfer.files;

            for (const file of files) {
                const formData = new FormData();
                formData.append('file', file);
                const formattedFile = formData;
                let fileDetails = {};

                if (file) {
                    fileDetails = {
                        name: file.name,
                        size: file.size,
                        content_type: file.type,
                        content_id: file.name,
                        extension: '',
                        isFileLoaded: true,
                    };
                    await EventBus.emit(
                        `richText-${this.hashPanel}:loadingAttachments`,
                        fileDetails,
                    );
                }

                ApiClient.post('/email/attachment', {}, formattedFile)
                    .then((response) => {
                        if (response.data) {
                            const fileInfo = response.data;
                            const parsedFile = {
                                name: fileInfo.name,
                                size: fileInfo.size,
                                extension: fileInfo.extension,
                                content_id: fileInfo.content_id,
                                content_type: fileInfo.content_type,
                                content_disposition: 'attachment',
                                public_link: fileInfo.public_link,
                            };
                            EventBus.emit(`richText-${this.hashPanel}:addAttachment`, parsedFile);
                        }
                    })
                    .catch((error) => {
                        const errorMessage = error.response.data.error.message;
                        const errorResponse = {
                            name: fileDetails.name,
                            size: fileDetails.size,
                            error_text: errorMessage,
                            error: true,
                            error_message: errorMessage,
                        };

                        if (typeof errorMessage === 'object') {
                            for (const message of Object.values(errorMessage)) {
                                errorResponse.error_text = message;
                                errorResponse.error_message = message;
                                EventBus.emit(`richText-${this.hashPanel}:addAttachment`, {
                                    ...errorResponse,
                                });
                            }
                        } else {
                            EventBus.emit(`richText-${this.hashPanel}:addAttachment`, {
                                ...errorResponse,
                            });
                        }
                    });
            }
        },
        handleVisibleChange(visibility) {
            if (!visibility && this.showDropdown) {
                this.showDropdown = false;
            }
        },
        openScheduleEmail() {
            this.showScheduleEmailDialog = true;
        },
    },
};
</script>

<style lang="scss" scoped>
.rich-text {
    position: relative;
}

.drag-text {
    height: 100%;
    width: 100%;
    position: absolute;
    top: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--colors-common-text-standard-default);
    border: 1px dashed var(--colors-common-border-focus);
    border-radius: var(--spacing-0);
    background-color: var(--colors-common-background-selected-hover);
    opacity: 0.9;
}
</style>

<style lang="scss">
#compass-dropdown-container {
    .email-panel__schedule-dropdown {
        width: 220px;
        z-index: 1000;
    }
}

.email-richtext-menu {
    .menu {
        display: flex;
        gap: var(--spacing-0);
    }
}

.email-panel {
    background-color: var(--colors-common-background-default);
    border-radius: 4px;
    box-shadow: 0 0 8px 0 rgba(17, 17, 17, 0.2);
    display: flex;
    flex-direction: column;
    max-height: 720px;
    overflow: hidden;
    position: relative;
    transition: height 0.3s ease;
    width: 720px;

    &__header {
        align-items: center;
        align-self: stretch;
        background: var(--colors-common-background-darkest-default);
        display: flex;
        gap: var(--spacing-5);
        padding: var(--spacing-1) var(--spacing-3);

        &-title {
            color: var(--colors-common-text-standard-on-color);
            flex-grow: 1;
        }

        &-actions {
            display: flex;
            gap: var(--spacing-1);
        }
    }

    &__content {
        display: flex;
        height: 100%;
        flex-direction: column;

        &-subject {
            margin: 0 0 var(--spacing-2) 0;
        }

        .email-richtext {
            flex: 1;
            position: relative;

            .compass-form-item,
            .compass-form-item__input {
                display: initial;
            }

            .tiptap.ProseMirror:not(.email-consult-editor .tiptap.ProseMirror) {
                height: 100%;
            }
        }

        .email-recipients-inputs {
            margin-bottom: var(--spacing-1);
        }
    }

    &__footer {
        display: flex;
        flex-direction: column;

        padding: 0 0 var(--spacing-3) 0;

        .email-richtext-menu {
            padding: var(--spacing-1) var(--spacing-5);
            border-bottom: 1px solid var(--colors-common-border-default);
        }

        &-container {
            align-items: center;
            color: var(--colors-common-text-subtle-default);
            display: flex;
            justify-content: space-between;
            gap: var(--spacing-3);
            padding: var(--spacing-3) var(--spacing-5) 0;
        }

        .container-actions {
            display: flex;
            gap: var(--spacing-3);
            margin-left: auto;
        }
    }

    &__ai-hint {
        color: var(--colors-common-text-subtle-default);
    }

    &.minimized {
        align-self: end;
        border-radius: 4px 4px 0 0;
        max-width: 220px;

        .email-panel__header {
            padding: var(--spacing-1) var(--spacing-2);

            &-title {
                align-items: center;
                display: flex;
                gap: var(--spacing-1);
            }
        }
    }

    .tiptap {
        .is-empty {
            &:before {
                color: var(--colors-common-text-standard-placeholder);
                content: attr(data-placeholder);
                float: left;
                height: 0;
                pointer-events: none;
            }
        }
        p:has(+ [data-node-view-wrapper]) {
            &.is-empty {
                &:before {
                    content: none;
                }
            }
        }
    }

    img {
        max-width: 100%;
        height: auto;
    }
}
</style>
